import { useLocation } from "wouter";
import { Sparkles, Pencil } from "lucide-react";
import { useQuery } from "@tanstack/react-query";
import { useAuth } from "@/hooks/use-auth";
import { useLifestylePictures } from "@/hooks/use-lifestyle-pictures";
import { apiRequest } from "@/lib/queryClient";
import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";
import { cn } from "@/lib/utils";
import LifestylePicsGrid from "@/components/lifestyle-pics/lifestyle-pics-grid";
import { ProfileMarkdown } from "@/components/profile-markdown";

type SidekickProfileResponse = {
  professional_summary?: string | null;
  why_me_for_you?: string | null;
  industry_experience?: string | null;
  education_and_certification?: string | null;
  fun_fact?: string | null;
  favourite_quote?: string | null;
  skills?: string | null;
  tools_and_tech?: string | null;
  languages?: string | null;
  hobbies?: string | null;
  profile_picture_url?: string | null;
  calendar_booking_url?: string | null;
};

function getInitials(name?: string | null) {
  if (!name) return "?";
  return name
    .split(" ")
    .map((n) => n[0])
    .join("")
    .toUpperCase()
    .slice(0, 2);
}

function splitComma(s?: string | null): string[] {
  if (!s) return [];
  return s.split(",").map((x) => x.trim()).filter(Boolean);
}

/**
 * One label/value row.
 *
 * Eight accent colours on eight coloured icon tiles were the loudest thing on
 * the dashboard, and none of the colours meant anything — rose for "why me",
 * amber for "industry experience" encoded no relationship. The label alone
 * carries it, in the same 10.5px uppercase the figures strip uses.
 */
function Field({ label, value }: { label: string; value: string }) {
  return (
    <div className="border-b border-border-faint py-3 last:border-b-0">
      <p className="mb-1.5 text-[10.5px] font-bold uppercase tracking-[0.07em] text-muted-foreground">
        {label}
      </p>
      <ProfileMarkdown
        source={value}
        className="text-[12.5px] leading-[1.55] text-slate-600 dark:text-slate-300"
      />
    </div>
  );
}

/** The tracked profile fields, in the order they render. */
const PROFILE_FIELDS = [
  "professional_summary",
  "why_me_for_you",
  "industry_experience",
  "education_and_certification",
  "skills",
  "tools_and_tech",
  "languages",
  "hobbies",
  "fun_fact",
  "favourite_quote",
  "profile_picture_url",
  "calendar_booking_url",
] as const;

function completeness(profile: SidekickProfileResponse | null | undefined): number {
  if (!profile) return 0;
  const filled = PROFILE_FIELDS.filter((key) => {
    const value = profile[key];
    return typeof value === "string" && value.trim().length > 0;
  }).length;
  return Math.round((filled / PROFILE_FIELDS.length) * 100);
}

export default function ProfileSection({ className }: { className?: string }) {
  const { user } = useAuth();
  const [, setLocation] = useLocation();

  const profileQuery = useQuery({
    queryKey: ["sidekick_profile"],
    staleTime: 45 * 60 * 1000,
    queryFn: async (): Promise<SidekickProfileResponse | null> => {
      try {
        const res = await apiRequest("GET", "/sidekick_profile/me");
        return res.json();
      } catch (err) {
        if (err instanceof Error && err.message.startsWith("404:")) return null;
        throw err;
      }
    },
  });

  const { data: pictures = [], isLoading: isLoadingPics } = useLifestylePictures();

  const profile = profileQuery.data;
  const hasProfile = Boolean(
    profile && (
      profile.professional_summary ||
      profile.why_me_for_you ||
      profile.industry_experience ||
      profile.education_and_certification ||
      profile.fun_fact ||
      profile.favourite_quote ||
      profile.skills ||
      profile.tools_and_tech ||
      profile.hobbies ||
      profile.calendar_booking_url ||
      profile.profile_picture_url
    )
  );
  const isEmpty = !hasProfile && pictures.length === 0;
  const isLoading = profileQuery.isLoading || isLoadingPics;
  const tools = splitComma(profile?.tools_and_tech);
  const languages = splitComma(profile?.languages);

  const percent = completeness(profile);

  return (
    <section
      className={cn(
        "flex flex-col rounded-lg border border-border-subtle bg-card",
        className,
      )}
    >
      <div className="flex items-center justify-between gap-3 border-b border-border-subtle px-[18px] py-3">
        <h2 className="text-sm font-bold tracking-[-0.01em] text-foreground">
          Your profile
        </h2>
        {!isEmpty && (
          <div className="flex items-center gap-2.5">
            <span
              className={cn(
                "text-[11.5px] font-semibold",
                percent >= 80
                  ? "text-emerald-700 dark:text-emerald-400"
                  : "text-muted-foreground",
              )}
            >
              {percent}% complete
            </span>
            <Button
              variant="outline"
              size="sm"
              className="h-7 gap-1.5 rounded-lg px-2.5 text-xs font-semibold"
              onClick={() => setLocation("/settings?section=profile")}
            >
              <Pencil className="h-3 w-3" />
              Edit
            </Button>
          </div>
        )}
      </div>

      <div className="min-h-0 flex-1 overflow-y-auto">
        {isLoading ? (
          <div className="p-[18px]">
            <Skeleton className="h-48 rounded-lg" />
          </div>
        ) : isEmpty ? (
          <div className="flex flex-col items-center justify-center px-6 py-10 text-center">
            <div className="mb-3 flex h-12 w-12 items-center justify-center rounded-full bg-muted">
              <Sparkles className="h-5 w-5 text-muted-foreground" />
            </div>
            <p className="mb-1 text-sm font-medium text-foreground">
              Tell us about yourself
            </p>
            <p className="max-w-xs text-xs text-muted-foreground">
              Add a profile summary and a handful of fun photos so clients get a
              feel for who you are.
            </p>
            <Button
              size="sm"
              className="mt-4"
              onClick={() => setLocation("/settings?section=profile")}
            >
              Complete your profile
            </Button>
          </div>
        ) : (
          <>
            {/* Identity */}
            <div className="flex items-center gap-3 border-b border-border-faint px-[18px] py-3.5">
              <div className="flex h-[46px] w-[46px] shrink-0 items-center justify-center overflow-hidden rounded-full border border-border bg-muted text-sm font-semibold text-muted-foreground">
                {profile?.profile_picture_url ? (
                  <img
                    src={profile.profile_picture_url}
                    alt={user?.name ?? "Profile"}
                    className="h-full w-full object-cover"
                  />
                ) : (
                  getInitials(user?.name)
                )}
              </div>
              <div className="min-w-0">
                <p className="truncate text-sm font-bold tracking-[-0.01em] text-foreground">
                  {user?.name ?? "—"}
                </p>
                <p className="text-xs text-muted-foreground">
                  Personal Assistant
                </p>
              </div>
            </div>

            {/* Lifestyle pics */}
            <div className="border-b border-border-faint px-[18px] py-3.5">
              <p className="mb-2 text-[10.5px] font-bold uppercase tracking-[0.07em] text-muted-foreground">
                A peek into your life
              </p>
              <LifestylePicsGrid pictures={pictures} />
              {pictures.length < 5 && (
                <button
                  type="button"
                  onClick={() => setLocation("/settings")}
                  className="mt-2 text-xs font-medium text-primary hover:underline"
                >
                  Add {5 - pictures.length} more{" "}
                  {pictures.length === 4 ? "photo" : "photos"} →
                </button>
              )}
            </div>

            <div className="px-[18px] pb-3">
              {profile?.professional_summary && (
                <Field label="Summary" value={profile.professional_summary} />
              )}
              {profile?.why_me_for_you && (
                <Field label="Why me for you" value={profile.why_me_for_you} />
              )}
              {profile?.industry_experience && (
                <Field
                  label="Industry experience"
                  value={profile.industry_experience}
                />
              )}
              {profile?.education_and_certification && (
                <Field
                  label="Education & certification"
                  value={profile.education_and_certification}
                />
              )}
              {profile?.skills && <Field label="Skills" value={profile.skills} />}
              {profile?.hobbies && (
                <Field label="Hobbies" value={profile.hobbies} />
              )}
              {profile?.fun_fact && (
                <Field label="Fun fact" value={profile.fun_fact} />
              )}
              {profile?.favourite_quote && (
                <Field
                  label="Favourite quote"
                  value={`“${profile.favourite_quote}”`}
                />
              )}

              {tools.length > 0 && (
                <div className="border-b border-border-faint py-3 last:border-b-0">
                  <p className="mb-2 text-[10.5px] font-bold uppercase tracking-[0.07em] text-muted-foreground">
                    Tools &amp; tech
                  </p>
                  <div className="flex flex-wrap gap-1.5">
                    {tools.map((t) => (
                      <span
                        key={t}
                        className="inline-flex h-[22px] items-center rounded-full bg-muted px-2.5 text-[11.5px] font-medium text-slate-600 dark:text-slate-300"
                      >
                        {t}
                      </span>
                    ))}
                  </div>
                </div>
              )}

              {languages.length > 0 && (
                <div className="border-b border-border-faint py-3 last:border-b-0">
                  <p className="mb-2 text-[10.5px] font-bold uppercase tracking-[0.07em] text-muted-foreground">
                    Languages spoken
                  </p>
                  <div className="flex flex-wrap gap-1.5">
                    {languages.map((l) => (
                      <span
                        key={l}
                        className="inline-flex h-[22px] items-center rounded-full bg-muted px-2.5 text-[11.5px] font-medium text-slate-600 dark:text-slate-300"
                      >
                        {l}
                      </span>
                    ))}
                  </div>
                </div>
              )}

              {profile?.calendar_booking_url && (
                <div className="border-b border-border-faint py-3 last:border-b-0">
                  <p className="mb-1.5 text-[10.5px] font-bold uppercase tracking-[0.07em] text-muted-foreground">
                    Booking link
                  </p>
                  <a
                    href={profile.calendar_booking_url}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="break-all text-[12.5px] font-medium text-primary underline-offset-2 hover:underline"
                  >
                    {profile.calendar_booking_url}
                  </a>
                </div>
              )}
            </div>
          </>
        )}
      </div>
    </section>
  );
}
